// Date field UI: When (presets / custom offset / fixed date), Format (plain-English choices), live preview, Advanced.
//
// A date field is "printer clock + offset" — it cannot hold a calendar date. "Fixed date" therefore turns the
// field into plain text (e.g. 26.08.2026), exactly how the real labels store any fixed text.
import { h, toast } from '../dom.js';
import * as D from '../../itf/dates.js';
import { convertDateToFixedText } from '../../itf/label.js';
import { S, mutate } from '../state.js';
import { section, row, full, intInput, textInput, selectInput, message, toggles, errorLine } from './controls.js';

const FIXED = 'fixed';
const customOpen = new Set(); // field ids whose Custom panel the user opened explicitly
const fixedOpen = new Set(); // field ids whose Fixed date panel is open
const idOf = (f) => f.id ?? f.key;
const shortPreset = (label) => label.replace(/^Today \+\s*/, '+');
const pad2 = (n) => String(n).padStart(2, '0');
// Local calendar parts, never toISOString (that shifts the day across the UTC boundary).
const isoLocal = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const MIN_YEAR = 2000;
const MAX_YEAR = 2099;

/** `YYYY-MM-DD` (a date input's value) → local Date at noon, or null if not a real date in range. */
function parsePicked(value) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || '');
  if (!m) return null;
  const [y, mo, da] = [Number(m[1]), Number(m[2]), Number(m[3])];
  if (y < MIN_YEAR || y > MAX_YEAR) return null;
  const d = new Date(y, mo - 1, da, 12);
  return d.getFullYear() === y && d.getMonth() === mo - 1 && d.getDate() === da ? d : null;
}

export function dateSection(b, ctx) {
  const f = () => ctx.cur();
  const offset = () => D.normalizeOffset(f()?.offset);
  const safeOffset = () => {
    try {
      return offset();
    } catch {
      return null;
    }
  };
  const setOffset = (patch, tag) => ctx.edit({ offset: { ...offset(), ...patch } }, tag);
  const isFixed = () => !!f() && fixedOpen.has(idOf(f()));
  const isCustom = () => !!f() && !isFixed() && (customOpen.has(idOf(f())) || D.presetFor(offset()) === D.CUSTOM_PRESET);

  // ----- When -----
  const chips = h('div', { class: 'chip-row', role: 'radiogroup', 'aria-label': 'When' });
  const chipBtns = [];
  const addChip = (id, label, title, onPick) => {
    const btn = h('button', { class: 'chip', type: 'button', role: 'radio', title }, label);
    btn.addEventListener('click', onPick);
    chips.appendChild(btn);
    chipBtns.push([id, btn]);
  };
  const chipErr = errorLine();
  for (const p of D.PRESETS) {
    addChip(p.id, shortPreset(p.label), p.label, () => {
      customOpen.delete(idOf(f()));
      fixedOpen.delete(idOf(f()));
      chipErr.report(ctx.edit({ offset: { ...p.offset } }));
      b.sync();
    });
  }
  addChip(D.CUSTOM_PRESET, 'Custom', 'Set years, months and days to add to the print date', () => {
    customOpen.add(idOf(f()));
    fixedOpen.delete(idOf(f()));
    b.sync();
    const first = customPanel.querySelector('input');
    if (first) first.focus();
  });
  addChip(FIXED, 'Fixed date', 'Print one exact date instead of following the printer clock', () => {
    fixedOpen.add(idOf(f()));
    customOpen.delete(idOf(f()));
    b.sync();
    dateInput.focus();
  });
  b.add(() => {
    if (!f()) return;
    const active = isFixed() ? FIXED : isCustom() ? D.CUSTOM_PRESET : D.presetFor(offset());
    for (const [id, btn] of chipBtns) {
      btn.classList.toggle('on', id === active);
      btn.setAttribute('aria-checked', String(id === active));
    }
  });

  const hoursDetails = h(
    'details',
    null,
    h('summary', null, 'Advanced'),
    h('div', { style: { marginTop: '8px' } }, row('Hours', intInput(b, { ariaLabel: 'Offset hours', get: () => offset().hours, set: (v) => setOffset({ hours: v }, 'hours'), min: -9999, max: 9999, unit: 'h' }))),
  );
  const customPanel = h(
    'div',
    { style: { display: 'grid', gap: '8px' } },
    h(
      'div',
      { style: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '6px' } },
      ...[
        ['Years', 'years', 'y'],
        ['Months', 'months', 'mo'],
        ['Days', 'days', 'd'],
      ].map(([label, k, unit]) =>
        h('label', { style: { display: 'grid', gap: '3px', fontSize: '10px', color: 'var(--ink-3)' } }, label, intInput(b, { ariaLabel: `Offset ${k}`, get: () => offset()[k], set: (v) => setOffset({ [k]: v }, k), unit })),
      ),
    ),
    hoursDetails,
  );
  b.add(() => {
    if (!f()) return;
    customPanel.hidden = !isCustom();
    if (offset().hours && !hoursDetails.open) hoursDetails.open = true;
  });
  const describe = h('div', { class: 'hint' });
  b.add(() => {
    if (!f()) return;
    describe.textContent = D.describeOffset(offset());
    describe.hidden = isFixed();
  });

  // ----- Offsets that are really a typed date (e.g. 2026 / 8 / 26 in Years / Months / Days) -----
  const typoText = h('div', { class: 'hint', style: { color: 'var(--warn)' } });
  const typoBtn = h('button', { class: 'btn primary', type: 'button' });
  const typoBox = h('div', { role: 'alert', style: { display: 'grid', gap: '6px', justifyItems: 'start' } }, typoText, typoBtn);
  typoBtn.addEventListener('click', () => {
    const g = D.typedDateInOffset(safeOffset());
    if (!g) return;
    dateInput.value = `${g.year}-${pad2(g.month)}-${pad2(g.day)}`;
    fixedOpen.add(idOf(f()));
    customOpen.delete(idOf(f()));
    b.sync();
    dateInput.focus();
  });
  b.add(() => {
    const o = f() && safeOffset();
    const far = o ? D.suspiciousOffset(o) : null;
    typoBox.hidden = !far || isFixed();
    if (!far) return;
    typoText.textContent = far;
    const g = D.typedDateInOffset(o);
    typoBtn.hidden = !g;
    if (g) typoBtn.textContent = `Use ${pad2(g.day)}.${pad2(g.month)}.${g.year} as a fixed date`;
  });

  // ----- Fixed date: print one exact calendar date (saved as plain text) -----
  const dateInput = h('input', { class: 'input', type: 'date', 'aria-label': 'Date to print', min: `${MIN_YEAR}-01-01`, max: `${MAX_YEAR}-12-31`, value: isoLocal(new Date()) });
  const fixedPreview = h('div', { role: 'status', 'aria-live': 'polite', style: { fontFamily: 'var(--font-mono)', fontSize: '15px', color: 'var(--ink-1)', fontWeight: '500', overflowWrap: 'anywhere' } });
  const fixedErr = errorLine();
  const applyBtn = h('button', { class: 'btn primary', type: 'button' }, 'Set fixed date');
  const resolveFixed = () => {
    const picked = parsePicked(dateInput.value);
    if (!picked) return { ok: false, text: '', reason: `Pick a date between ${MIN_YEAR} and ${MAX_YEAR}.` };
    return D.fixedDateText(f()?.format ?? '', picked, f()?.language || 'ENGLISH');
  };
  dateInput.addEventListener('input', () => {
    fixedErr.report(null);
    b.sync();
  });
  applyBtn.addEventListener('click', () => {
    const cur = f();
    const res = resolveFixed();
    if (!cur || !res.ok) {
      fixedErr.report(new Error(res.reason || 'Pick a date first.'));
      return;
    }
    try {
      mutate((label) => convertDateToFixedText(label, cur.key, res.text));
    } catch (err) {
      if (!err || err.name !== 'LabelError') console.error(err);
      fixedErr.report(err instanceof Error ? err : new Error(String(err)));
      return;
    }
    fixedOpen.delete(idOf(cur));
    toast(`${cur.name} now prints ${res.text} every time`, 'good');
    const others = S.fields.filter((x) => x.kind === 'date' && x.key !== cur.key).map((x) => x.name);
    if (others.length) toast(`Still following the printer clock: ${others.join(', ')}`, 'warn');
  });
  const fixedPanel = h(
    'div',
    { style: { display: 'grid', gap: '8px' } },
    h('label', { style: { display: 'grid', gap: '3px', fontSize: '10px', color: 'var(--ink-3)' } }, 'Date to print', dateInput),
    fixedPreview,
    h('div', { class: 'hint' }, 'Prints this exact date every time. It is saved as plain text, so it no longer follows the printer clock and the field becomes a Text field. Undo brings the date field back.'),
    applyBtn,
    fixedErr.el,
  );
  b.add(() => {
    if (!f()) return;
    fixedPanel.hidden = !isFixed();
    if (!isFixed()) return;
    const res = resolveFixed();
    fixedPreview.textContent = res.ok ? `Prints exactly: ${res.text}` : res.reason || '—';
    fixedPreview.style.color = res.ok ? 'var(--ink-1)' : 'var(--warn)';
    applyBtn.disabled = !res.ok;
  });

  // ----- Format -----
  const formatOptions = () => {
    const cur = f()?.format ?? '';
    const list = D.FORMAT_CHOICES.map((c) => ({ value: c.value, label: `${c.label}${c.verified ? '' : ' ·'}` }));
    if (cur && !D.choiceFor(cur)) list.push({ value: cur, label: `Custom: ${cur}` });
    return list;
  };
  const badge = h('span');
  b.add(() => {
    if (!f()) return;
    const c = D.choiceFor(f().format);
    const verified = c ? c.verified : false;
    badge.className = verified ? 'status-verified' : 'status-unverified';
    badge.textContent = verified ? 'test-printed' : 'not yet test-printed';
    badge.title = c ? c.note : 'Custom format string — not yet test-printed on the SmartDate X65';
  });

  // ----- Preview -----
  const prints = h('div', { style: { fontFamily: 'var(--font-mono)', fontSize: '15px', color: 'var(--ink-1)', fontWeight: '500', userSelect: 'text', overflowWrap: 'anywhere' } });
  const when = h('div', { class: 'hint' });
  const note = h('div', { class: 'hint', style: { color: 'var(--warn)' } });
  const card = h(
    'div',
    { role: 'status', 'aria-live': 'polite', style: { background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: 'var(--r2)', padding: '10px 12px', display: 'grid', gap: '2px' } },
    h('div', { class: 'hint', style: { textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: '10px' } }, 'Prints as'),
    prints,
    when,
    note,
  );
  b.add(() => {
    if (!f()) return;
    card.hidden = isFixed();
    let p;
    try {
      p = D.previewLines(f(), new Date());
    } catch (err) {
      p = { value: '—', printDate: '—', approximate: true, prompted: false, description: String(err.message) };
    }
    prints.textContent = p.value || '—';
    when.textContent = `when printed today (${p.printDate || '—'})`;
    const notes = [];
    if (p.prompted) notes.push('The operator enters the offset at the printer.');
    if (p.approximate) notes.push('Preview is approximate — the printer may show something slightly different.');
    note.textContent = notes.join(' ');
    note.hidden = !notes.length;
  });

  // ----- Advanced: custom format, language, prompting -----
  const fmtMsg = { errors: [], warnings: [] };
  let draft = null;
  const fmtField = textInput(b, {
    ariaLabel: 'Custom format string',
    placeholder: '%D.%M.%Y',
    get: () => (draft !== null ? draft : f()?.format),
    set: (v) => {
      draft = v;
      const res = D.validateFormat(v);
      fmtMsg.errors = res.errors || [];
      fmtMsg.warnings = res.warnings || [];
      if (res.ok) {
        draft = null;
        return ctx.edit({ format: v }, 'format');
      }
      b.sync();
      return null;
    },
  });
  const fmtInput = fmtField.control;
  fmtInput.style.fontFamily = 'var(--font-mono)';
  fmtInput.addEventListener('blur', () => {
    draft = null;
    fmtMsg.errors = [];
    b.sync();
  });
  const advanced = h(
    'details',
    null,
    h('summary', null, 'Advanced'),
    h(
      'div',
      { style: { display: 'grid', gap: '8px', marginTop: '8px' } },
      row('Format code', fmtField),
      message(b, () => {
        if (fmtMsg.errors.length) return { kind: 'bad', text: fmtMsg.errors.join(' · ') };
        const w = f() ? D.validateFormat(f().format ?? '').warnings || [] : [];
        return w.length ? { kind: 'warn', text: w.join(' · ') } : null;
      }, { role: 'alert' }),
      row(
        'Language',
        selectInput(b, {
          ariaLabel: 'Month and day names language',
          options: () => {
            const cur = f()?.language;
            const list = D.LANGUAGES.map((l) => ({ value: l, label: l.charAt(0) + l.slice(1).toLowerCase().replace(/_/g, ' ') }));
            if (cur && !D.LANGUAGES.includes(cur)) list.push({ value: cur, label: cur });
            return list;
          },
          get: () => f()?.language || 'ENGLISH',
          set: (v) => ctx.edit({ language: v }),
        }),
      ),
      full(h('div', { class: 'hint' }, 'Language only changes three-letter month and day names.')),
      full(
        toggles(b, [
          {
            label: 'Ask operator for the offset at the printer',
            title: 'PromptingBehaviour = PROMPT_YY_MM_DD',
            get: () => f()?.prompting === 'PROMPT_YY_MM_DD',
            set: (v) => ctx.edit({ prompting: v ? 'PROMPT_YY_MM_DD' : 'DO_NOT_PROMPT' }),
          },
        ]),
      ),
    ),
  );
  b.add(() => {
    if (f() && !D.choiceFor(f().format) && !advanced.open) advanced.open = true;
  });

  return section('Date', 'date', [
    full(h('div', { class: 'hint', style: { marginBottom: '-2px' } }, 'When'), chips, describe, chipErr.el),
    customPanel,
    typoBox,
    fixedPanel,
    full(
      h('div', { style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '-2px' } }, h('span', { class: 'hint' }, 'Format'), badge),
      selectInput(b, { ariaLabel: 'Date format', options: formatOptions, get: () => f()?.format, set: (v) => ctx.edit({ format: v }) }),
    ),
    full(card),
    advanced,
  ]);
}
